import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useAnalyzeNoteMutation,
  useConvertNoteItemMutation,
  useGetNoteConfigQuery,
  useGetNoteQuery,
  useUpdateNoteMutation,
} from '@/redux/internal-services/notesApi';
import { useGetPropertiesQuery } from '@/redux/internal-services/workspaceApi';
import type { CreateTaskInput } from '@/redux/internal-services/workspaceApiExtensions';
import { SuggestionCards } from '@/components/notes/SuggestionCards';
import { NoteTraceability } from '@/components/notes/NoteTraceability';
import { IconSparkle } from '@/components/notes/icons';
import { NoteAudioPlayer } from '@/components/notes/NoteAudioPlayer';
import { noteTitle, relativeTime } from '@/components/notes/noteUtils';
import type { Rec } from '@/components/chat/ChatPills';

export type NoteDetailTab = 'summary' | 'transcript' | 'suggestions';

const IconTrash = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4">
    <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" strokeLinecap="round" />
  </svg>
);

interface Props {
  noteId: string;
  onDelete?: () => void;
  onClose?: () => void;
  /** When provided, the tab is controlled from outside (e.g. by SAM). */
  activeTab?: NoteDetailTab;
  onTabChange?: (tab: NoteDetailTab) => void;
  /** Open an already-created follow-up record inline in the workspace (no modal). */
  onOpenTask?: (taskId: string) => void;
  onNoteSynced?: (note: Rec | null) => void;
}

export function NoteDetailContent({ noteId, onDelete, onClose, activeTab, onTabChange, onOpenTask, onNoteSynced }: Props) {
  const { t } = useTranslation();
  const { data: note, isLoading, isError } = useGetNoteQuery(noteId);
  const { data: config } = useGetNoteConfigQuery();
  const { data: properties = [] } = useGetPropertiesQuery();
  const [analyzeNote, { isLoading: analyzing }] = useAnalyzeNoteMutation();
  const [updateNote, { isLoading: saving }] = useUpdateNoteMutation();
  const [convertItem] = useConvertNoteItemMutation();
  const [included, setIncluded] = useState<Record<string, boolean>>({});
  const [suggestionFields, setSuggestionFields] = useState<Record<string, CreateTaskInput>>({});
  const [creatingSelected, setCreatingSelected] = useState(false);

  const [internalTab, setInternalTab] = useState<NoteDetailTab>('summary');
  const tab = activeTab ?? internalTab;
  const setTab = (t2: NoteDetailTab) => { setInternalTab(t2); onTabChange?.(t2); };

  const [title, setTitle] = useState('');
  const [transcript, setTranscript] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [ctxType, setCtxType] = useState('');
  const [ctxId, setCtxId] = useState('');
  const lastSyncedOn = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!note || note.updated_on === lastSyncedOn.current) return;
    lastSyncedOn.current = note.updated_on;
    setTitle(noteTitle(note));
    setTranscript(note.clean_transcript || note.final_transcript || '');
    setTags(note.custom_tags || []);
    setCtxType(note.context?.context_type || '');
    setCtxId(note.context?.entity_id || '');
    onNoteSynced?.(note as unknown as Rec);
  }, [note, onNoteSynced]);

  const onNoteSyncedRef = useRef(onNoteSynced);
  onNoteSyncedRef.current = onNoteSynced;
  const editReadyRef = useRef(false);
  useEffect(() => {
    if (!note) return;
    if (!editReadyRef.current) { editReadyRef.current = true; return; }
    const timer = setTimeout(() => {
      const p = properties.find((pp) => pp.property_id === ctxId);
      onNoteSyncedRef.current?.({
        ...note,
        title,
        clean_transcript: transcript,
        custom_tags: tags,
        context: ctxType && ctxId ? { context_type: ctxType, entity_id: ctxId, display_label: p?.address?.display_line ?? null } : null,
      } as unknown as Rec);
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, transcript, tags, ctxType, ctxId]);

  useEffect(() => () => onNoteSyncedRef.current?.(null), []);

  useEffect(() => {
    setIncluded((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const s of note?.suggestions || []) {
        if (!(s.suggestion_id in next)) { next[s.suggestion_id] = true; changed = true; }
      }
      return changed ? next : prev;
    });
  }, [note?.suggestions]);

  const allTags = config?.classification_tags || [];

  const transcriptDirty = !!note && transcript.trim() !== (note.clean_transcript || note.final_transcript || '').trim();
  const dirty = !!note && (
    title !== noteTitle(note) ||
    transcriptDirty ||
    tags.join(',') !== (note.custom_tags || []).join(',') ||
    ctxType !== (note.context?.context_type || '') ||
    ctxId !== (note.context?.entity_id || '')
  );

  const noteType = useMemo(() => {
    const platform = ['site_observation', 'inspection', 'measurement', 'meeting_note', 'follow_up', 'general'];
    return tags.find((tg) => platform.includes(tg)) || (ctxType === 'meeting' ? 'meeting_note' : note?.note_type || 'general');
  }, [tags, ctxType, note?.note_type]);

  const toggleTag = (tag: string) =>
    setTags((prev) => (prev.includes(tag) ? prev.filter((x) => x !== tag) : [...prev, tag]));

  if (isError) {
    return <p className="p-4 text-sm text-ui-text3">{t('noteTaker.loadError', 'Note could not be loaded.')}</p>;
  }
  if (isLoading || !note) {
    return <p className="p-4 text-sm text-ui-text3">{t('noteTaker.loading')}</p>;
  }

  const openSuggestions = (note.suggestions || []).filter((s) => !s.dismissed);
  const pendingSuggestions = openSuggestions.filter((s) => !s.confirmed);
  const isVoice = note.source === 'voice';

  const handleSave = async () => {
    const p = properties.find((pp) => pp.property_id === ctxId);
    await updateNote({
      id: noteId,
      body: {
        title: title.trim(),
        clean_transcript: transcript,
        note_type: noteType,
        custom_tags: tags,
        context: ctxType && ctxId ? { context_type: ctxType, entity_id: ctxId, display_label: p?.address?.display_line ?? null } : undefined,
      },
    }).unwrap().catch(() => undefined);
  };

  const handleReanalyze = async () => {
    await updateNote({ id: noteId, body: { clean_transcript: transcript } }).unwrap().catch(() => undefined);
    await analyzeNote(noteId).unwrap().catch(() => undefined);
  };

  const handleCreateSelected = async () => {
    const toCreate = pendingSuggestions.filter((s) => included[s.suggestion_id] ?? true);
    setCreatingSelected(true);
    try {
      await Promise.allSettled(
        toCreate.map((s) =>
          convertItem({
            id: noteId,
            suggestionId: s.suggestion_id,
            target: s.target || 'task',
            fields: (suggestionFields[s.suggestion_id] || {}) as unknown as Record<string, unknown>,
          }).unwrap(),
        ),
      );
    } finally {
      setCreatingSelected(false);
    }
  };

  const TABS: { id: NoteDetailTab; label: string; badge?: number }[] = [
    { id: 'summary',     label: t('noteTaker.tabsDetail.summary',     'Summary') },
    { id: 'transcript',  label: t('noteTaker.tabsDetail.transcript',  'Transcript') },
    { id: 'suggestions', label: t('noteTaker.tabsDetail.suggestions', 'Suggestions'), badge: pendingSuggestions.length || undefined },
  ];

  return (
    <div className="flex h-full flex-col">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 pt-4 flex-shrink-0">
        {onClose ? (
          <button type="button" onClick={onClose} className="flex items-center gap-1.5 text-sm text-ui-text3 hover:text-ui-text lg:hidden">
            ← {t('noteTaker.back')}
          </button>
        ) : (
          <span />
        )}
        {onDelete && (
          <button type="button" onClick={onDelete} className="flex h-8 w-8 items-center justify-center rounded-lg border border-ui-border text-ui-text3 hover:border-red-500 hover:text-red-500" aria-label={t('noteTaker.delete')}>
            <IconTrash />
          </button>
        )}
      </div>

      {/* Identity */}
      <div className="px-4 pt-3 flex-shrink-0">
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t('noteTaker.titlePlaceholder')}
          className="block w-full bg-transparent text-lg font-bold text-ui-text outline-none placeholder:text-ui-text3/60"
        />
        <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-ui-text3">
          {note.context?.context_type && (
            <span>{t(`noteTaker.contexts.${note.context.context_type}`, { defaultValue: note.context.context_type })} ·</span>
          )}
          <span>{relativeTime(note.created_on)}</span>
        </div>
      </div>

      {/* Tab bar */}
      <div className="mx-4 mt-3 flex gap-0 rounded-xl border border-ui-border bg-ui-subtle p-1 flex-shrink-0">
        {TABS.map(({ id, label, badge }) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={`relative flex-1 py-2 text-xs font-semibold rounded-lg transition-colors ${
              tab === id
                ? 'bg-ui-card text-brand-dark shadow-sm border border-ui-border'
                : 'text-ui-text3 hover:text-ui-text'
            }`}
          >
            {label}
            {!!badge && (
              <span className="absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-brand-bright px-1 text-[10px] font-bold text-white">
                {badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="flex-1 min-h-0">

        {/* ── Summary tab ── */}
        {tab === 'summary' && (
          <div className="flex h-full flex-col gap-4 overflow-y-auto no-scrollbar overscroll-contain p-4">
            {/* AI summary */}
            <div className="rounded-xl border border-ui-border bg-ui-subtle p-4">
              <p className="text-sm font-semibold text-ui-text">{t('noteTaker.aiSummary')}</p>
              <p className="mt-1.5 text-sm leading-relaxed text-ui-text">{note.summary || t('noteTaker.noSummary')}</p>
              <button
                type="button"
                disabled={analyzing}
                onClick={handleReanalyze}
                className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-brand-dark px-3.5 py-2 text-xs font-semibold text-white transition hover:brightness-90 disabled:opacity-50"
              >
                <IconSparkle className="h-3.5 w-3.5" /> {t('noteTaker.reanalyze')}
              </button>
            </div>

            {/* Audio player */}
            {isVoice && note.audio_url && (
              <div>
                <p className="mb-2 text-xs font-bold uppercase tracking-wide text-ui-text3">{t('noteTaker.audio')}</p>
                <NoteAudioPlayer src={note.audio_url} />
              </div>
            )}

            {/* Tags */}
            <div>
              <p className="mb-2 text-xs font-bold uppercase tracking-wide text-ui-text3">{t('noteTaker.tags')}</p>
              <div className="flex flex-wrap gap-1.5">
                {allTags.map((tag) => {
                  const on = tags.includes(tag);
                  return (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => toggleTag(tag)}
                      className={`rounded-full border px-2.5 py-1 text-xs transition ${
                        on ? 'border-brand-bright bg-brand-bright/10 text-brand-dark' : 'border-ui-border text-ui-text3 hover:bg-ui-hover'
                      }`}
                    >
                      {t(`noteTaker.tagLabels.${tag}`, tag.replace(/_/g, ' '))}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Details rows */}
            <div>
              <p className="mb-2 text-xs font-bold uppercase tracking-wide text-ui-text3">{t('noteTaker.tabsDetail.details')}</p>
              <div className="flex flex-col gap-2.5 text-sm">
                <Row label={t('noteTaker.noteType')} value={isVoice ? t('noteTaker.tabs.voice') : t('noteTaker.tabs.text')} />
                <Row label={t('noteTaker.statusLabel')} value={t(`noteTaker.status.${note.status}`, { defaultValue: note.status })} />
                <Row label={t('noteTaker.created')} value={new Date(note.created_on).toLocaleString()} />
                <Row label={t('noteTaker.updated')} value={new Date(note.updated_on).toLocaleString()} />
              </div>
            </div>
          </div>
        )}

        {/* ── Transcript tab ── */}
        {tab === 'transcript' && (
          <div className="flex h-full flex-col p-4 gap-3">
            <textarea
              value={transcript}
              onChange={(e) => setTranscript(e.target.value)}
              className="flex-1 min-h-0 w-full resize-none rounded-xl border border-ui-border bg-ui-input p-3 text-sm leading-relaxed text-ui-text"
            />
            {transcriptDirty && (
              <div className="flex items-center justify-between gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-2.5 flex-shrink-0">
                <p className="text-xs text-amber-700">{t('noteTaker.editedReanalyze', 'Transcript edited — re-analyze to refresh the summary and follow-ups.')}</p>
                <button
                  type="button"
                  disabled={analyzing || !transcript.trim()}
                  onClick={handleReanalyze}
                  className="shrink-0 rounded-md bg-amber-600 px-2.5 py-1 text-xs font-medium text-white disabled:opacity-50"
                >
                  {analyzing ? t('noteTaker.analysing', 'Analysing…') : t('noteTaker.reanalyze')}
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── Suggestions tab ── */}
        {tab === 'suggestions' && (
          <div className="flex h-full flex-col gap-4 overflow-y-auto no-scrollbar overscroll-contain p-4">
            {/* Link to property */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="flex flex-col gap-1">
                <span className="text-xs font-bold uppercase tracking-wide text-ui-text3">{t('noteTaker.linkContext')}</span>
                <select
                  className="rounded-xl border border-ui-border bg-ui-input p-2.5 text-sm text-ui-text [color-scheme:light] dark:[color-scheme:dark]"
                  value={ctxType}
                  onChange={(e) => { setCtxType(e.target.value); setCtxId(''); }}
                >
                  <option value="">{t('noteTaker.noContext')}</option>
                  <option value="property">{t('noteTaker.contexts.property', 'Property')}</option>
                </select>
              </label>
              {ctxType === 'property' && (
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-bold uppercase tracking-wide text-ui-text3">{t('noteTaker.contexts.property', 'Property')}</span>
                  <select
                    className="rounded-xl border border-ui-border bg-ui-input p-2.5 text-sm text-ui-text [color-scheme:light] dark:[color-scheme:dark]"
                    value={ctxId}
                    onChange={(e) => setCtxId(e.target.value)}
                  >
                    <option value="">{t('noteTaker.selectProperty', 'Select a property…')}</option>
                    {properties.map((p) => (
                      <option key={p.property_id} value={p.property_id}>
                        {p.address?.normalized_address || [
                          p.address?.street_number, p.address?.street_name, p.address?.street_suffix,
                        ].filter(Boolean).join(' ')}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>

            {/* Follow-up suggestions */}
            <div>
              <p className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-ui-text3">
                {t('noteTaker.tabsDetail.suggestions')}
                {openSuggestions.length > 0 && (
                  <span className="rounded-full bg-brand-bright/20 px-1.5 text-[11px] font-semibold text-brand-dark">{openSuggestions.length}</span>
                )}
              </p>
              {openSuggestions.length > 0 ? (
                <>
                  <SuggestionCards
                    noteId={noteId}
                    suggestions={note.suggestions}
                    included={included}
                    onToggleIncluded={(id) => setIncluded((prev) => ({ ...prev, [id]: !(prev[id] ?? true) }))}
                    onFieldsChange={(id, body) => setSuggestionFields((prev) => ({ ...prev, [id]: body }))}
                    onOpenTask={onOpenTask}
                  />
                  {pendingSuggestions.length > 0 && (
                    <div className="flex justify-end pt-1">
                      <button
                        type="button"
                        disabled={creatingSelected}
                        onClick={handleCreateSelected}
                        className="rounded-lg bg-brand-dark px-4 py-2 text-sm font-semibold text-white transition hover:brightness-90 disabled:opacity-50"
                      >
                        {creatingSelected ? t('noteTaker.creating', 'Creating…') : t('noteTaker.createSelected', 'Create selected')}
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-sm text-ui-text3">{t('noteTaker.noSuggestions')}</p>
              )}
            </div>

            {/* Traceability */}
            <div>
              <p className="mb-2 text-xs font-bold uppercase tracking-wide text-ui-text3">{t('noteTaker.tabsDetail.traceability')}</p>
              <NoteTraceability noteId={noteId} />
            </div>
          </div>
        )}
      </div>

      {/* Save — appears only once something has actually changed */}
      {dirty && (
        <div className="border-t border-ui-border p-3 flex-shrink-0">
          <button
            type="button"
            disabled={saving}
            onClick={handleSave}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand-dark py-2.5 text-sm font-semibold text-white transition hover:brightness-90 disabled:opacity-50"
          >
            {saving ? t('noteTaker.analysing', 'Saving…') : t('noteTaker.saveNote')}
          </button>
        </div>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-dashed border-ui-border pb-2">
      <span className="text-xs font-medium text-ui-text3">{label}</span>
      <span className="text-right text-sm text-ui-text">{value}</span>
    </div>
  );
}

export default NoteDetailContent;
